import {
  createContext,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { TSectionName } from '@bridebook/web/lib/wedding-website/types';

const SYNC_TIMEOUT_MS = 1000;
const SCROLL_DELAY_MS = 250;

export type TPreviewSections =
  | 'overview'
  | 'giftList'
  | 'hotels'
  | 'ourStory'
  | 'faq'
  | 'venue'
  | 'schedule';

export type TDetailsSections =
  | 'overview'
  | 'hotels'
  | 'photos'
  | 'faq'
  | 'venue'
  | 'schedule'
  | 'ourStory'
  | 'giftList';

interface ISectionRefs {
  preview: Record<TPreviewSections, HTMLElement | null>;
  details: Record<TDetailsSections, HTMLElement | null>;
}
interface EditorSyncContextValue {
  sectionRefs: MutableRefObject<ISectionRefs>;
  syncedPreviewSection: TPreviewSections | null;
  triggerPreviewSync: (section: TPreviewSections | null) => void;
  syncedDetailsSection: TDetailsSections | null;
  triggerDetailsSync: (section: TDetailsSections | null) => void;
  registerPreviewRef: (sectionId: TPreviewSections, element: HTMLElement | null) => void;
  registerDetailsRef: (sectionId: TDetailsSections, element: HTMLElement | null) => void;
}

const EditorSyncContext = createContext<EditorSyncContextValue | undefined>(undefined);

/**
 * Provider component for editor synchronization context that manages section syncing
 * between preview and details views in the wedding website editor.
 *
 * Handles automatic timeout-based clearing of synced sections and provides
 * ref registration for DOM elements that need to be synchronized.
 *
 * @param props - The component props
 * @param props.noop - When true, creates a dummy context that performs no operations.
 *                     This allows shared code to be reused between mobile and desktop
 *                     environments where mobile doesn't require actual syncing functionality.
 * @param props.onPreviewSync - Optional callback that's called when preview sync is triggered.
 *                              Can be used for additional actions when preview sections are synced.
 * @param props.onDetailsSync - Optional callback that's called when details sync is triggered.
 *                              Used to switch to the Details tab when editing from the panel view.
 * @param props.children - React child nodes to be wrapped by the provider
 *
 * @example
 * ```tsx
 * // Desktop usage with full functionality
 * <EditorSyncProvider
 *   onDetailsSync={() => setActiveTab(WeddingWebsiteDesktopTabs.Details)}
 *   onPreviewSync={() => console.log('Preview synced')}>
 *   <EditorComponent />
 * </EditorSyncProvider>
 *
 * // Mobile usage with no-op context
 * <EditorSyncProvider noop={true}>
 *   <SharedEditorComponent />
 * </EditorSyncProvider>
 * ```
 */
export const EditorSyncProvider = ({
  noop = false,
  onPreviewSync,
  onDetailsSync,
  children,
}: {
  noop?: boolean;
  onPreviewSync?: () => void;
  onDetailsSync?: () => void;
  children: React.ReactNode;
}) => {
  const [syncedPreviewSection, setSyncedPreviewSection] = useState<TPreviewSections | null>(null);
  const [syncedDetailsSection, setSyncedDetailsSection] = useState<TDetailsSections | null>(null);

  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const detailsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const sectionRefs = useRef<ISectionRefs>({
    preview: {
      overview: null,
      giftList: null,
      hotels: null,
      ourStory: null,
      faq: null,
      venue: null,
      schedule: null,
    },
    details: {
      overview: null,
      hotels: null,
      photos: null,
      faq: null,
      venue: null,
      schedule: null,
      ourStory: null,
      giftList: null,
    },
  });

  const registerPreviewRef = useCallback(
    (sectionId: TPreviewSections, element: HTMLElement | null) => {
      sectionRefs.current.preview[sectionId] = element;
    },
    [],
  );

  const registerDetailsRef = useCallback(
    (sectionId: TDetailsSections, element: HTMLElement | null) => {
      sectionRefs.current.details[sectionId] = element;
    },
    [],
  );

  const triggerSync = useCallback(
    (
      section: TPreviewSections | TDetailsSections | null,
      timeoutRef: MutableRefObject<NodeJS.Timeout | null>,
      setSyncedSection: (section: any) => void,
    ) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set the active section
      setSyncedSection(section);

      // If section is not null, schedule it to be reset after 1000ms
      if (section !== null) {
        timeoutRef.current = setTimeout(() => {
          setSyncedSection(null);
          timeoutRef.current = null;
        }, SYNC_TIMEOUT_MS);
      }
    },
    [],
  );

  const triggerPreviewSync = useCallback(
    (section: TPreviewSections | null) => {
      triggerSync(section, previewTimeoutRef, setSyncedPreviewSection);
      if (section !== null && onPreviewSync) {
        onPreviewSync();
      }
    },
    [triggerSync, onPreviewSync],
  );

  const triggerDetailsSync = useCallback(
    (section: TDetailsSections | null) => {
      triggerSync(section, detailsTimeoutRef, setSyncedDetailsSection);
      if (section !== null && onDetailsSync) {
        onDetailsSync();
      }
    },
    [triggerSync, onDetailsSync],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
      if (detailsTimeoutRef.current) {
        clearTimeout(detailsTimeoutRef.current);
      }
    },
    [],
  );

  // For shared mobile/desktop code, we use noop context when on mobile (no syncing there)
  const contextValue = noop
    ? {
        sectionRefs,
        syncedPreviewSection: null,
        triggerPreviewSync: () => {
          /** no-op */
        },
        syncedDetailsSection: null,
        triggerDetailsSync: () => {
          /** no-op */
        },
        registerPreviewRef: () => {
          /** no-op */
        },
        registerDetailsRef: () => {
          /** no-op */
        },
      }
    : {
        sectionRefs,
        syncedPreviewSection,
        triggerPreviewSync,
        syncedDetailsSection,
        triggerDetailsSync,
        registerPreviewRef,
        registerDetailsRef,
      };

  return <EditorSyncContext.Provider value={contextValue}>{children}</EditorSyncContext.Provider>;
};

/** Context hook for the EditorSync (to read/set context values) */
export const useEditorSyncContext = () => {
  const context = useContext(EditorSyncContext);
  if (context === undefined) {
    throw new Error('useEditorSyncContext must be used within an EditorSyncProvider');
  }
  return context;
};

const scrollElementWithinPreviewContainer = (element: HTMLElement) => {
  const scrollContainer = element.closest<HTMLElement>('#preview-scroll-container');
  if (!scrollContainer) return false;

  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const scrollTop = scrollContainer.scrollTop;

  // Account for CSS transform scale - getBoundingClientRect returns scaled values
  // but scrollTop/scrollHeight are unscaled
  const scale = containerRect.width / scrollContainer.clientWidth || 1;
  const unscaledOffset = (elementRect.top - containerRect.top) / scale;

  // Calculate target scroll, capped at max scroll to prevent overscrolling into empty space
  const targetScroll = scrollTop + unscaledOffset;
  const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
  const boundedScroll = Math.min(targetScroll, maxScroll);

  scrollContainer.scrollTo({ top: boundedScroll, behavior: 'smooth' });
  return true;
};

/** Hook that syncs the editor preview and sidebar scroll */
export const useEditorScrollSync = () => {
  const { syncedPreviewSection, syncedDetailsSection, sectionRefs } = useEditorSyncContext();

  useEffect(() => {
    if (!syncedPreviewSection) return;

    const element = sectionRefs.current.preview[syncedPreviewSection];
    if (!element) return;

    if (!scrollElementWithinPreviewContainer(element)) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [syncedPreviewSection, sectionRefs]);

  useEffect(() => {
    if (syncedDetailsSection) {
      const element = sectionRefs.current.details[syncedDetailsSection];
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, SCROLL_DELAY_MS);
      }
    }
  }, [syncedDetailsSection, sectionRefs]);
};

export const useEditorDetailsSync = (id: string, callback: () => void) => {
  const { syncedDetailsSection } = useEditorSyncContext();

  useEffect(() => {
    if (syncedDetailsSection === id) {
      callback();
    }
  }, [syncedDetailsSection, id, callback]);
};

export const mapSectionNameToPreviewSection = (
  sectionName: TSectionName,
): TPreviewSections | null => {
  switch (sectionName) {
    case 'coupleNames':
    case 'weddingDate':
    case 'rsvp':
      return 'overview';
    case 'giftList':
      return 'giftList';
    case 'hotels':
      return 'hotels';
    case 'ourStory':
      return 'ourStory';
    case 'faq':
      return 'faq';
    case 'primaryVenue':
      return 'venue';
    case 'schedule':
      return 'schedule';
    default:
      return null;
  }
};
